import React from 'react';
import { useTranslation } from 'react-i18next';
import { StudentAbsenceSummary } from '../../../../types/absenceTypes';
import ImageWithBasePath from '../../../../components/Common/imageWithBasePath';
import Badge from '../../../../components/Common/ui/Badge';
import ImageWithoutBasePath from 'components/Common/imageWithoutBasePath';

interface StudentAbsenceCardProps {
  student: StudentAbsenceSummary;
}

const StudentAbsenceCard: React.FC<StudentAbsenceCardProps> = ({ student }) => {
  const { t } = useTranslation();
  
  const excusedPercentage = student.totalAbsenceHours > 0 
    ? (student.excusedHours / student.totalAbsenceHours) * 100 
    : 0;
  
  const pendingPercentage = student.totalAbsenceHours > 0 
    ? (student.pendingHours / student.totalAbsenceHours) * 100 
    : 0;

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex align-items-center mb-3">
          <div className="avatar me-3">
            <ImageWithoutBasePath
              src={student.student.user.photo || 'assets/img/profiles/avatar-01.jpg'}
              alt={`${student.student.user.firstName} ${student.student.user.lastName}`}
              className="rounded-circle"
              width={50}
            />
          </div>
          <div>
            <h5 className="mb-0">
              {student.student.user.firstName} {student.student.user.lastName}
            </h5>
            <small className="text-muted">{student.student.class?.name}</small>
          </div>
        </div>
        
        <div className="d-flex justify-content-between mb-2">
          <span>{t('total_hours')} :</span>
          <span className="font-weight-bold">{student.totalAbsenceHours}</span>
        </div>
        
        <div className="progress mb-3" style={{ height: '8px' }}>
          <div
            className="progress-bar bg-success"
            role="progressbar"
            style={{ width: `${excusedPercentage}%` }}
            aria-valuenow={excusedPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            title={`${t('excused')}: ${student.excusedHours} ${t('hours')}`}
          ></div>
          <div
            className="progress-bar bg-warning"
            role="progressbar"
            style={{ width: `${pendingPercentage}%` }}
            aria-valuenow={pendingPercentage}
            aria-valuemin={0}
            aria-valuemax={100}
            title={`${t('pending')} : ${student.pendingHours} ${t('hours')}`}
          ></div>
        </div>
        
        <div className="d-flex justify-content-between small">
          <div className="d-flex align-items-center">
            <span
              className="d-inline-block bg-success rounded-circle me-2"
              style={{ width: '10px', height: '10px' }}
            />
            {t('excused')}: {student.excusedHours}
          </div>

          <div className="d-flex align-items-center">
            <span
              className="d-inline-block bg-warning rounded-circle me-2"
              style={{ width: '10px', height: '10px' }}
            />
            {t('pending')}: {student.pendingHours}
          </div>
        </div>
        
        <div className="mt-3 pt-2 border-top">
          <div className="d-flex justify-content-between align-items-center">
            <span>{t('threshold')} : </span>
            <Badge 
              variant={student.percentageOfThreshold >= 0.9 
                ? 'danger' 
                : student.percentageOfThreshold >= 0.8 
                  ? 'warning' 
                  : 'info'}
              text={`${Math.round(student.percentageOfThreshold * 100)}%`}
              translateText={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentAbsenceCard;
